import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import UserSearchWidget from '~/components/Common/UserSearchWidget';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useExpireVoucherUser } from '~/hooks/apis/promo/useExpireVoucherUser';
import { useListVoucherUsers } from '~/hooks/apis/promo/useListVoucherUsers';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import GridPagination from '../Common/Elements/GridPagination';
import PageSubheader from '../Common/Elements/PageSubheader';

type ExistingVoucherUser = definitions['VoucherUser'];

type Props = {
  userIds: string[];
  onChange: (userIds: string[]) => void;
  expiredAt: string;
  onExpiredAtChange: (expiredAt: string) => void;
  disabled?: boolean;
  voucherId?: string;
  showAdvanceDescription?: boolean;
};

export default function VoucherUsers({
  userIds,
  onChange,
  expiredAt,
  onExpiredAtChange,
  showAdvanceDescription,
  disabled = false,
  voucherId,
}: Props) {
  const normalizeExpiredAt = useCallback((value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }
    return trimmedValue.includes('T') ? trimmedValue : `${trimmedValue}T00:00:00.000Z`;
  }, []);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchValue, setUserSearchValue] = useState('');
  const [expireTarget, setExpireTarget] = useState<ExistingVoucherUser | null>(null);

  const { data: existingUsersResponse, isLoading: isExistingLoading } = useListVoucherUsers({
    voucherId,
    query: { page: 0, limit: 10 },
  });
  const existingUsers = existingUsersResponse?.result?.voucherUsers ?? [];
  const existingUserIds = useMemo(() => {
    return Array.from(new Set(existingUsers.map((user) => user.userId).filter(Boolean))).sort();
  }, [existingUsers]);
  const { data: usersSummaryMap, isLoading: isUsersSummaryLoading } = useGetUsersSummaryByIds({
    userIds: existingUserIds,
    brand,
  });
  const resolvedUsersSummaryMap = usersSummaryMap ?? new Map();

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const expireVoucherUserMutation = useExpireVoucherUser();
  const hasSetDefaultExpiry = useRef(false);

  useEffect(() => {
    if (voucherId && !expiredAt && !hasSetDefaultExpiry.current) {
      hasSetDefaultExpiry.current = true;
      onExpiredAtChange(dayjs().add(1, 'year').format('YYYY-MM-DD'));
    }
  }, [expiredAt, onExpiredAtChange, voucherId]);

  const handleAdd = useCallback(async () => {
    if (!selectedUserId) {
      enqueueSnackbar('Select a user to add', { variant: 'error' });
      return;
    }

    if (!voucherId) {
      onChange([...userIds, selectedUserId]);
      setSelectedUserId('');
      setUserSearchValue('');
      return;
    }

    try {
      const resolvedExpiredAt = expiredAt.trim()
        ? normalizeExpiredAt(expiredAt)
        : normalizeExpiredAt(dayjs().add(1, 'year').format('YYYY-MM-DD'));
      await createVoucherUsersMutation.mutateAsync({
        voucher_id: voucherId,
        expired_at: resolvedExpiredAt,
        voucher_users: [{ user_id: selectedUserId }],
      });
      enqueueSnackbar('Added voucher user', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      setSelectedUserId('');
      setUserSearchValue('');
    } catch (error) {
      enqueueSnackbar(`Failed to add voucher user: ${(error as Error).message}`, { variant: 'error' });
    }
  }, [
    createVoucherUsersMutation,
    enqueueSnackbar,
    onChange,
    queryClient,
    selectedUserId,
    userIds,
    voucherId,
    normalizeExpiredAt,
    expiredAt,
  ]);

  const handleExpireConfirm = useCallback(async () => {
    if (!expireTarget) {
      return;
    }
    try {
      await expireVoucherUserMutation.mutateAsync(expireTarget.voucherUserId);
      enqueueSnackbar('Expired voucher user', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
    } catch (error) {
      enqueueSnackbar(`Failed to expire voucher user: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setExpireTarget(null);
    }
  }, [enqueueSnackbar, expireTarget, expireVoucherUserMutation, queryClient, voucherId]);

  // Handle form submit (Enter key) to add user when a user is selected
  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (selectedUserId && !disabled && !createVoucherUsersMutation.isPending) {
        handleAdd();
      }
    },
    [selectedUserId, disabled, createVoucherUsersMutation.isPending, handleAdd],
  );

  const existingColumns = useMemo<Array<GridColDef<ExistingVoucherUser>>>(
    () => [
      {
        field: 'userName',
        headerName: 'Name',
        minWidth: 200,
        flex: 1,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) =>
          resolvedUsersSummaryMap.get(params.row.userId)?.full_name ?? '-',
      },
      {
        field: 'userEmail',
        headerName: 'Email',
        minWidth: 240,
        flex: 1,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) =>
          resolvedUsersSummaryMap.get(params.row.userId)?.email ?? '-',
      },
      { field: 'userId', headerName: 'User ID', minWidth: 260, flex: 1 },
      {
        field: 'createdAt',
        headerName: 'Created At',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => DateFormatterDetailed({ date: params.value }),
      },
      { field: 'usedAt', headerName: 'Used At', minWidth: 180, flex: 1 },
      {
        field: 'expiredAt',
        headerName: 'Expired At',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => DateFormatterDetailed({ date: params.value, highlightIfPast: true }),
      },
      {
        field: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) => (
          <IconButton
            aria-label="Expire voucher user"
            size="small"
            onClick={() => setExpireTarget(params.row)}
            disabled={disabled || expireVoucherUserMutation.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [disabled, expireVoucherUserMutation.isPending, resolvedUsersSummaryMap],
  );

  return (
    <Box id="users">
      <PageSubheader title={`Users (${existingUsers?.length ?? 0})`}>
        {showAdvanceDescription && (
          <Box component="span" color="text.secondary" alignItems="left">
            Users that qualify for this voucher. The voucher is not editable if it has users associated.
          </Box>
        )}
      </PageSubheader>
      {!voucherId ? (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Create the voucher to add users.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Add Voucher Users
          </Typography>
          <form onSubmit={handleFormSubmit}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <TextField
                label="Expires At"
                type="date"
                value={expiredAt}
                onChange={(event) => onExpiredAtChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                sx={{ minWidth: 240 }}
              />
              <Box sx={{ flex: 1 }}>
                <UserSearchWidget
                  id="voucher-user-search"
                  label="Name / Email / Customer Support Code / User ID"
                  formErrors={[]}
                  onChange={(_, value) => setSelectedUserId(value)}
                  passedAutocompleteValue={userSearchValue}
                  setPassedAutocompleteValue={setUserSearchValue}
                  disabled={disabled}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={disabled || createVoucherUsersMutation.isPending}
              >
                Add users
              </Button>
            </Stack>
          </form>
        </>
      )}

      {voucherId && (
        <Box mb={3} mt={3}>
          <DataGrid
            autoHeight
            rows={existingUsers}
            columns={existingColumns}
            slots={{
              pagination: GridPagination,
            }}
            getRowId={(row) => row.voucherUserId}
            disableRowSelectionOnClick
            hideFooter
            loading={isExistingLoading || isUsersSummaryLoading}
            sx={{ backgroundColor: 'background.paper' }}
          />
        </Box>
      )}

      <Dialog open={Boolean(expireTarget)} onClose={() => setExpireTarget(null)}>
        <DialogTitle>Expire Voucher User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Expire voucher access for user {expireTarget?.userId}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setExpireTarget(null)} disabled={expireVoucherUserMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExpireConfirm}
            disabled={expireVoucherUserMutation.isPending}
          >
            Expire
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
